import type * as ents from '@attain-apps/entities'
import Fuse from 'fuse.js'

import type { Feature } from './feature'

export class Selectors {
  public readonly checkInStage
  public readonly checkInSourcePosition
  public readonly eligibleForDailyCheckIn

  public readonly checkInCurrentMood
  public readonly checkInCurrentMoodCandidates

  public readonly checkIn
  public readonly checkInIsLoading
  public readonly checkInError
  public readonly title
  public readonly prompt
  public readonly ignoreCheckInPrompt

  public readonly suggestedResponses
  public readonly response
  public readonly responseToTheResponse
  public readonly responseToTheResponseIsLoading
  public readonly responseToTheResponseError

  public readonly checkInOrResponseToTheResponseIsLoading

  public readonly feedBeforeSearch
  public readonly feedFuse
  public readonly feed
  public readonly feedSearchQuery
  public readonly feedIsLoading
  public readonly feedError

  constructor(feature: Feature) {
    this.checkInStage = feature.state.project(state => state.checkInStage)
    this.checkInSourcePosition = feature.state.project(state =>
      state.checkInSourcePosition?.(),
    )
    this.eligibleForDailyCheckIn = feature.state.project(
      state => state.eligibleForDailyCheckIn,
    )
    this.checkInCurrentMood = feature.state.project(
      state => state.checkInCurrentMood,
    )
    this.checkInCurrentMoodCandidates = feature.state.project(
      state => state.checkInCurrentMoodCandidates,
    )
    this.checkIn = feature.state.project(state => state.checkIn)
    this.checkInIsLoading = feature.state.project(
      state => state.checkInIsLoading,
    )
    this.checkInError = feature.state.project(state => state.checkInError)
    this.title = this.checkIn.project(checkIn => checkIn?.title)
    this.prompt = this.checkIn.project(checkIn => checkIn?.prompt)
    this.ignoreCheckInPrompt = feature.state.project(
      state => state.ignoreCheckInPrompt,
    )

    this.suggestedResponses = this.checkIn.project(
      checkIn => checkIn?.suggestedResponses,
    )
    this.response = feature.state.project(state => state.response)
    this.responseToTheResponse = feature.state.project(
      state => state.responseToTheResponse,
    )
    this.responseToTheResponseIsLoading = feature.state.project(
      state => state.responseToTheResponseIsLoading,
    )
    this.responseToTheResponseError = feature.state.project(
      state => state.responseToTheResponseError,
    )

    this.checkInOrResponseToTheResponseIsLoading =
      this.checkInIsLoading.project(
        this.responseToTheResponseIsLoading.asSelector,
        (checkInIsLoading, responseToTheResponseIsLoading) =>
          checkInIsLoading || responseToTheResponseIsLoading,
      )

    this.feedBeforeSearch = feature.state.project(state => state.feed)
    this.feedFuse = this.feedBeforeSearch.project(
      feed =>
        new Fuse(feed, {
          keys: [
            'prompt',
            'response',
            'from.name',
          ] satisfies readonly Key.DotSeparated.That.Extends<
            string | undefined,
            ents.core.summit.CheckIn.Response
          >[],
        }),
    )
    this.feedSearchQuery = feature.state.project(state => state.feedSearchQuery)
    this.feed = this.feedBeforeSearch.project(
      this.feedFuse.asSelector,
      this.feedSearchQuery.asSelector,
      (feedBeforeSearch, feedFuse, feedSearchQuery) => {
        if (!feedSearchQuery) {
          return feedBeforeSearch
        }

        return feedFuse.search(feedSearchQuery).map(result => result.item)
      },
    )
    this.feedIsLoading = feature.state.project(state => state.feedIsLoading)
    this.feedError = feature.state.project(state => state.feedError)
  }
}
